import { useState, type KeyboardEvent, type Ref } from 'react';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

export const SEARCH_MAX_LENGTH = 255;

export interface SearchBarProps {
  value: string;
  /**
   * Called on every keystroke (and with `''` when cleared). With `submitOnEnter`,
   * called only with the trimmed term when the search is submitted or cleared.
   */
  onChange: (value: string) => void;
  /**
   * For searches that call the API: typing only edits the box, and the search runs
   * on Enter or the trailing search button. Once applied, that button becomes a
   * cancel button that clears the search.
   */
  submitOnEnter?: boolean;
  /** With `submitOnEnter`: the text in the box on every keystroke, e.g. to highlight matches before searching. */
  onInputChange?: (text: string) => void;
  /** Called with the trimmed term when the user presses Enter. */
  onSearch?: (value: string) => void;
  /** Called after the clear button empties the box. */
  onClear?: () => void;
  placeholder?: string;
  /** Accessible name. Defaults to the placeholder. */
  label?: string;
  maxLength?: number;
  autoFocus?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  inputRef?: Ref<HTMLInputElement>;
  /** Runs before the built-in Enter handling; call `preventDefault()` to skip it. */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  sx?: SxProps<Theme>;
  testId?: string;
}

/**
 * Standard search box: leading search icon, a clear button once there is
 * text, Enter to submit, and a length cap (pasted text is truncated too).
 * With `submitOnEnter`, a trailing search/cancel button replaces both icons.
 */
export function SearchBar({
  value,
  onChange,
  submitOnEnter = false,
  onInputChange,
  onSearch,
  onClear,
  placeholder = 'Search…',
  label,
  maxLength = SEARCH_MAX_LENGTH,
  autoFocus,
  fullWidth = true,
  size,
  inputRef,
  onKeyDown,
  sx,
  testId,
}: SearchBarProps) {
  // The text being typed; with `submitOnEnter`, `value` is the applied search.
  const [draft, setDraft] = useState(value);
  const [appliedValue, setAppliedValue] = useState(value);
  if (appliedValue !== value) {
    // The applied search changed from outside (e.g. a reset); show it in the box.
    setAppliedValue(value);
    setDraft(value);
  }
  const text = submitOnEnter ? draft : value;
  const applied = submitOnEnter && value !== '' && draft.trim() === value;

  const clear = () => {
    setDraft('');
    onInputChange?.('');
    onChange('');
    onClear?.();
  };
  const submit = () => {
    const term = draft.trim();
    setDraft(term);
    if (term !== value) onChange(term);
    onSearch?.(term);
  };

  const endAdornment = submitOnEnter ? (
    <InputAdornment position="end">
      {applied ? (
        <IconButton aria-label="Clear search" onClick={clear} size="small" edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : (
        <IconButton aria-label="Search" onClick={submit} size="small" edge="end">
          <SearchIcon fontSize="small" />
        </IconButton>
      )}
    </InputAdornment>
  ) : value ? (
    <InputAdornment position="end">
      <IconButton aria-label="Clear search" onClick={clear} size="small" edge="end">
        <CloseIcon fontSize="small" />
      </IconButton>
    </InputAdornment>
  ) : null;

  return (
    <TextField
      type="search"
      value={text}
      onChange={(e) => {
        const next = e.target.value.slice(0, maxLength);
        if (submitOnEnter) {
          setDraft(next);
          onInputChange?.(next);
          if (next === '' && value !== '') onChange('');
        } else onChange(next);
      }}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented || e.key !== 'Enter') return;
        if (submitOnEnter) {
          e.preventDefault();
          submit();
        } else if (onSearch) {
          e.preventDefault();
          onSearch(value.trim());
        }
      }}
      placeholder={placeholder}
      autoFocus={autoFocus}
      autoComplete="off"
      fullWidth={fullWidth}
      size={size}
      inputRef={inputRef}
      sx={sx}
      slotProps={{
        htmlInput: { maxLength, 'aria-label': label ?? placeholder, 'data-testid': testId },
        input: {
          // Hide the browser's own clear button; ours is themed and consistent.
          sx: { '& input::-webkit-search-cancel-button': { display: 'none' } },
          startAdornment: submitOnEnter ? null : (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment,
        },
      }}
    />
  );
}
